import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon } from '@/assets/icon/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icon/ChevronRightIcon';
import trendingIcon from '@/assets/images/trending.png';
import { inlineNavigationStyle } from '@/components/atoms/NavigationButton/Navigation-button.contants';
import NavigationButton from '@/components/atoms/NavigationButton/NavigationButton';
import Tag from '@/components/atoms/SearchTag/SearchTag';
import { TrendingNowProps } from '@/types';

import SearchSectionHeader from '../SearchSectionHeader/SearchSectionHeader';
import {
  TrendingNowContainer,
  TrendingNowTag,
  TrendingNowTagWrapper,
} from './TrendingNow.style';

const TrendingNow: React.FC<TrendingNowProps> = ({
  trendingKeywords,
  onClickTrendingKeyword,
  isNative,
  isWebMobileView = false,
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!isNative && scrollContainerRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth);
    }
  }, [isNative]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();

    const resizeObserver = new ResizeObserver(() => checkScrollPosition());
    resizeObserver.observe(container);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [trendingKeywords, checkScrollPosition]);

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const showButtons = !isNative && !isWebMobileView;

  const tagItems = trendingKeywords.map((trendingKeyword, idx) => (
    <Tag
      key={idx}
      label={trendingKeyword.query}
      onClick={() => onClickTrendingKeyword(trendingKeyword.query, idx)}
    />
  ));

  return (
    <TrendingNowContainer>
      <SearchSectionHeader
        title={t('search.trendingNow')}
        titleIcon={trendingIcon.src}
      />
      <TrendingNowTagWrapper>
        {showButtons && (
          <NavigationButton
            position="left"
            onClick={scrollLeft}
            aria-label="Scroll left"
            style={inlineNavigationStyle(canScrollLeft)}
          >
            <ChevronLeftIcon width={32} height={32} />
          </NavigationButton>
        )}
        <TrendingNowTag ref={scrollContainerRef} onScroll={checkScrollPosition}>
          {tagItems}
        </TrendingNowTag>
        {showButtons && (
          <NavigationButton
            position="right"
            onClick={scrollRight}
            aria-label="Scroll right"
            style={inlineNavigationStyle(canScrollRight)}
          >
            <ChevronRightIcon width={32} height={32} />
          </NavigationButton>
        )}
      </TrendingNowTagWrapper>
    </TrendingNowContainer>
  );
};

export default TrendingNow;
